import {
  AccordionGroup,
  AggregationsProvider,
  AssetsProvider,
  ConnectionStatusProvider,
  CurrenciesProvider,
  CurrencyConversionProvider,
  DEFAULT_LOCALE,
  ExposureDefinitionsProvider,
  GlobalDialogProvider,
  HeartbeatsProvider,
  IntlProvider,
  LoaderWrapper,
  MarketAccountCurrenciesProvider,
  MarketPriceContextProvider,
  NotesProvider,
  PowerSearchProvider,
  RefRateContextProvider,
  RefreshButton,
  SecuritiesProvider,
  SparklineProvider,
  UnifiedLiquidityProvider,
  composeComponents,
  isCypressWindow,
  logger,
  useUserContext,
} from '@talos/kyoko';

import { ConnectionMessage } from '@src/components/ConnectionMessage';
import { GlobalToasts, type GlobalToastsProps } from '@src/components/GlobalToasts';
import NavigationListener from '@src/components/NavigationListener';
import { TalosAdminHeaderWrapper } from '@src/components/TalosAdminHeader/TalosAdminHeaderWrapper';
import { AppConfigProvider } from '@src/providers/AppConfigProvider';
import { AppwideDrawerSettingsProvider } from '@src/providers/AppwideDrawerSettingsProvider';
import { AvaOMSSearchableSecuritiesProvider } from '@src/providers/AvaOMSSearchableSecuritiesProvider';
import { BlotterTableContextProvider } from '@src/providers/BlotterTableContextProvider';
import { CustomerBalanceProvider } from '@src/providers/CustomerBalancesProvider';
import { CustomerCreditProvider } from '@src/providers/CustomerCreditProvider';
import { CustomerSecuritiesProvider } from '@src/providers/CustomerSecuritiesProvider';
import { CustomersProvider } from '@src/providers/CustomersProvider';
import { ExecutionReportsProvider } from '@src/providers/ExecutionReportsProvider';
import { ExposuresProvider } from '@src/providers/ExposuresProvider';
import { FeesProvider } from '@src/providers/FeesProvider';
import { IntercomProvider } from '@src/providers/IntercomContext';
import { OrderInteractionsProvider } from '@src/providers/OrderInteractionsProvider';
import { OrdersProvider } from '@src/providers/OrdersProvider';
import { OrganizationProvider } from '@src/providers/OrganizationProvider';
import { PricingRulesProvider } from '@src/providers/PricingRulesProvider';
import { ProvidersGate } from '@src/providers/ProvidersGate';
import { QuotesProvider } from '@src/providers/QuotesProvider';
import { ServiceWorkerProvider } from '@src/providers/ServiceWorkerContext';
import { SoundProvider } from '@src/providers/SoundProvider';
import { StrategiesProvider } from '@src/providers/StrategiesProvider';
import { SubAccountRollupsProvider } from '@src/providers/SubAccountRollupsProvider';
import { SubAccountsProvider } from '@src/providers/SubAccountsProvider';
import { TradingLimitsProvider } from '@src/providers/TradingLimitsProvider';
import { TransfersProvider } from '@src/providers/TransfersProvider';
import { UserGroupsProvider } from '@src/providers/UserGroupsProvider';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { PropsWithChildren } from 'react';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { useIsMobileLayout } from '../hooks/useIsMobileLayout';
import { showcases } from '../tokens/showcase';
import type { TourName } from '../tokens/tours';
import { AdjustedSettlementDatesProvider } from './AdjustedSettlementDatesProvider';
import { AdminHeaderContextProvider } from './AdminHeaderContext';
import { BlotterTableStorageProvider } from './AppConfigRedux/BlotterTableStorageProvider';
import { AppInstallPromptProvider } from './AppInstallPromptProvider';
import { AppLayoutContextProvider } from './AppLayoutContextProvider/AppLayoutContextProvider';
import { AppNotificationsMonitorContainer } from './AppNotifications/AppNotificationsMonitorContainer';
import { AppReloadProvider } from './AppReloadContext';
import { AppStateProvider } from './AppStateProvider';
import type { AvaAppStore } from './AppStateProvider/AppStateProviderHooks';
import { AuxiliaryOrderDataProvider } from './AuxiliaryOrderDataProvider';
import { AvaAIProvider } from './AvaAIProvider';
import { AvaMarketAccountsProvider } from './AvaMarketAccountsProvider';
import { BasketsProvider } from './BasketsProvider';
import { BlockedSubscriptionsSyncer } from './BlockedSubscriptionsSyncer';
import { CoinMetricsCredentialsSyncer } from './CoinMetricsApiProvider/CoinMetricsCredentialsSyncer';
import { CoinMetricsSocketClientRegistrar } from './CoinMetricsApiProvider/useCoinMetricsSocketClient';
import { ContextBlotterFilterProvider } from './ContextBlotterFilterProvider';
import { CustomerMarketAccountsProvider } from './CustomerMarketAccountsProvider';
import { CustomerPricingTiersProvider } from './CustomerPricingTiersProvider';
import { EquityMarginRatioLimitProvider } from './EquityMarginRatioLimitProvider';
import { FixingIndexProvider } from './FixingIndexProvider';
import { FundDefinitionProvider } from './FundDefinitionProvider';
import { HedgePositionStatusProvider } from './HedgePositionStatusProvider';
import { HomeCurrencyContextProvider } from './HomeCurrencyProvider';
import { MarketAccountPermissionFiltersProvider } from './MarketAccountPermissionFiltersProvider';
import { MarketCurrenciesProvider } from './MarketCurrenciesProvider';
import { MarketExposuresProvider } from './MarketExposuresProvider';
import { MarketSecurityStatusesProvider } from './MarketSecurityStatusProvider';
import { MixpanelProvider } from './MixpanelProvider';
import { PortfolioAccountsProvider } from './PortfolioAccountsProvider';
import { PositionDetailsModalProvider } from './PositionDetailsModalProvider';
import { PredictionMarketsMixpanelMetadata } from './PredictionMarketsMixpanelMetadata';
import { PredictionsProvider } from './PredictionsProvider';
import { RefDataCacheProvider } from './RefDataCacheProvider';
import { ShowcaseProvider } from './ShowcaseProvider/ShowcaseProvider';
import { SubAccountPermissionFiltersProvider } from './SubAccountPermissionFiltersProvider';
import { SubAccountPositionLimitsProvider } from './SubAccountPositionLimitsProvider';
import { SubAccountRollupMembershipsProvider } from './SubAccountRollupMembershipsProvider';
import { SubAccountWindowLimitsProvider } from './SubAccountWindowLimitsProvider';
import { TourProviderWrapper } from './TourProviderWrapper';
import { UsersContextProvider } from './UsersContextProvider';

export const appQueryClient = new QueryClient();

const QueryProvider = ({ children }: PropsWithChildren) => (
  <QueryClientProvider client={appQueryClient}>{children}</QueryClientProvider>
);

const UnifiedLiquidityProviderWithFlags = ({ children }: PropsWithChildren) => {
  const { enableUnifiedLiquidityCrossInstrumentRequest } = useFeatureFlag();
  return (
    <UnifiedLiquidityProvider enableCrossInstrumentRequest={enableUnifiedLiquidityCrossInstrumentRequest}>
      {children}
    </UnifiedLiquidityProvider>
  );
};

const Providers = composeComponents([
  // App State Provider for useBlotterTable
  BlotterTableStorageProvider,

  IntercomProvider,
  MixpanelProvider,
  NotesProvider,
  AppInstallPromptProvider,

  ServiceWorkerProvider,
  RefDataCacheProvider,
  CurrenciesProvider,
  SecuritiesProvider,
  AvaOMSSearchableSecuritiesProvider,
  MarketAccountCurrenciesProvider,
  SubAccountRollupsProvider,
  SubAccountsProvider,
  UsersContextProvider,
  StrategiesProvider,
  OrganizationProvider,
  AvaAIProvider,
  UnifiedLiquidityProviderWithFlags,

  ProvidersGate,

  QueryProvider,
  AssetsProvider,
  MarketCurrenciesProvider,
  FixingIndexProvider,
  FundDefinitionProvider,
  MarketSecurityStatusesProvider,
  SubAccountRollupMembershipsProvider,
  SubAccountPermissionFiltersProvider,
  MarketAccountPermissionFiltersProvider,
  HeartbeatsProvider,
  FeesProvider,
  ConnectionStatusProvider,
  AuxiliaryOrderDataProvider,
  OrdersProvider,
  ExecutionReportsProvider,
  OrderInteractionsProvider,
  QuotesProvider,
  AggregationsProvider,
  HomeCurrencyContextProvider,
  CurrencyConversionProvider,
  PortfolioAccountsProvider,
  TransfersProvider,
  ExposureDefinitionsProvider,
  UserGroupsProvider,
  PricingRulesProvider,
  CustomersProvider,
  CustomerPricingTiersProvider,
  CustomerSecuritiesProvider,
  PredictionsProvider,
  PredictionMarketsMixpanelMetadata,
  CustomerBalanceProvider,
  CustomerMarketAccountsProvider,
  CustomerCreditProvider,
  TradingLimitsProvider,
  SubAccountPositionLimitsProvider,
  SubAccountWindowLimitsProvider,
  ExposuresProvider,
  MarketExposuresProvider,
  BlotterTableContextProvider,
  SoundProvider,
  AppwideDrawerSettingsProvider,
  PositionDetailsModalProvider,
  AppLayoutContextProvider,
  AccordionGroup,
  HedgePositionStatusProvider,
  EquityMarginRatioLimitProvider,
  BasketsProvider,
  ContextBlotterFilterProvider,
  AdjustedSettlementDatesProvider,
]);

export function ContextProvider({
  children,
  store,
  enableTours,
  tourNames,
  enableShowcases,
  onReload,
  renderToastOverride,
}: PropsWithChildren<{
  store?: AvaAppStore;
  /** For enabling tours in testing environments */
  enableTours?: boolean;
  /** For overriding the default tours in testing environments */
  tourNames?: TourName[];
  enableShowcases?: boolean;
  onReload?: () => void;
  renderToastOverride?: GlobalToastsProps['renderToastOverride'];
}>) {
  const { user, isLoaded: isUserLoaded } = useUserContext();
  const showAdminHeader = !useIsMobileLayout();

  // Only enable Tour popups in either non-cypress environments (e2e), non test environments or if explicitly enabled.
  const enableToursInner =
    enableTours || (!isCypressWindow(window) && import.meta.env.VITE_IS_TEST_ENVIRONMENT !== 'true');
  const enableShowcasesInner =
    enableShowcases || (!isCypressWindow(window) && import.meta.env.VITE_IS_TEST_ENVIRONMENT !== 'true');

  // This should never happen. UserContextProvider resolves to a defined User before ContextProvider is called.
  // Prevents Talos users from being stuck in faulty prodmon morphs.
  if (isUserLoaded && user == null) {
    logger.error(new Error(`User does not exist. ${window.location.href}`));
    return (
      <LoaderWrapper>
        User does not exist.
        <RefreshButton />
      </LoaderWrapper>
    );
  }

  if (user == null) {
    return null;
  }
  return (
    <GlobalDialogProvider>
      <BlockedSubscriptionsSyncer />
      <AppStateProvider user={user} store={store}>
        <CoinMetricsCredentialsSyncer />
        <CoinMetricsSocketClientRegistrar />
        <AppConfigProvider user={user}>
          <IntlProvider locale={DEFAULT_LOCALE}>
            <AppReloadProvider onReload={onReload}>
              <AdminHeaderContextProvider>
                {showAdminHeader && <TalosAdminHeaderWrapper onReload={onReload} />}
                <AvaMarketAccountsProvider>
                  <Providers>
                    <ConnectionMessage />
                    <AppNotificationsMonitorContainer />
                    <GlobalToasts renderToastOverride={renderToastOverride} />
                    <NavigationListener />
                    <ShowcaseProvider showcases={showcases} enable={enableShowcasesInner}>
                      <TourProviderWrapper enable={enableToursInner} tourNames={tourNames}>
                        <MarketPriceContextProvider>
                          <RefRateContextProvider>
                            <SparklineProvider>
                              <PowerSearchProvider>{children}</PowerSearchProvider>
                            </SparklineProvider>
                          </RefRateContextProvider>
                        </MarketPriceContextProvider>
                      </TourProviderWrapper>
                    </ShowcaseProvider>
                  </Providers>
                </AvaMarketAccountsProvider>
              </AdminHeaderContextProvider>
            </AppReloadProvider>
          </IntlProvider>
        </AppConfigProvider>
      </AppStateProvider>
    </GlobalDialogProvider>
  );
}
